/**
 * Email Service Configuration
 * Uses Firebase Trigger Email Extension (Firestore-based email sending)
 * 
 * Setup Instructions:
 * 1. Install extension: firebase ext:install firebase/firestore-send-email
 * 2. Configure SMTP URI (SendGrid/Gmail/Custom)
 * 3. Set default FROM email: noreply@dossiloc.ca
 * 4. Extension automatically sends emails when documents added to 'mail' collection
 * 
 * Benefits:
 * - Server-side email sending (no client API keys)
 * - Automatic retries and error handling
 * - Built-in delivery status tracking
 * - Complete audit trail in Firestore
 * - Auto-scales with Cloud Functions
 */

import { collection, addDoc, doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from './firebase';

interface EmailOptions {
  to: string;
  toName?: string;
  subject: string;
  body: string;
  from?: string;
  fromName?: string;
  // Optional metadata for tracking
  leadId?: string;
  generatedByAI?: boolean;
  sentBy?: string;
  sentByName?: string;
}

interface EmailResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

/**
 * Send email using Firebase Trigger Email Extension
 * Writes to Firestore 'mail' collection - extension handles actual sending
 */
export async function sendEmailWithFirebase(options: EmailOptions): Promise<EmailResult> {
  try {
    // Helper to remove undefined values
    const cleanObject = (obj: any): any => {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined && value !== null) {
          cleaned[key] = value;
        }
      }
      return cleaned;
    };

    // Prepare email document for Firebase extension
    const emailDoc = {
      to: [options.to],
      message: {
        subject: options.subject,
        text: options.body,
        html: options.body.replace(/\n/g, '<br>'),
      },
      from: options.from || 'noreply@dossiloc.ca',
      replyTo: 'hello@dossiloc.ca',
      // Custom metadata for tracking (remove undefined values)
      metadata: cleanObject({
        leadId: options.leadId,
        generatedByAI: options.generatedByAI,
        sentBy: options.sentBy,
        sentByName: options.sentByName,
        timestamp: Timestamp.now(),
        source: 'admin_dashboard',
      }),
    };

    // Write to Firestore - Firebase extension triggers automatically
    const docRef = await addDoc(collection(db, 'mail'), emailDoc);
    
    console.log('Email queued for delivery:', docRef.id);

    return {
      success: true,
      messageId: docRef.id, // Firestore document ID
    };
  } catch (error) {
    console.error('Error queueing email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Send email using SendGrid API (Legacy - kept for backward compatibility)
 * Prefer sendEmailWithFirebase() for production use
 */
export async function sendEmailWithSendGrid(options: EmailOptions): Promise<EmailResult> {
  console.warn('sendEmailWithSendGrid() is deprecated. Use sendEmailWithFirebase() instead.');
  
  const apiKey = import.meta.env.VITE_SENDGRID_API_KEY;
  
  if (!apiKey) {
    console.warn('SendGrid API key not configured - falling back to Firebase extension');
    return sendEmailWithFirebase(options);
  }

  try {
    const response = await fetch('https://api.sendgrid.com/v3/mail/send', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        personalizations: [
          {
            to: [
              {
                email: options.to,
                name: options.toName || options.to.split('@')[0],
              },
            ],
            subject: options.subject,
          },
        ],
        from: {
          email: options.from || 'noreply@dossiloc.ca',
          name: options.fromName || 'DossiLoc Sales Team',
        },
        content: [
          {
            type: 'text/plain',
            value: options.body,
          },
          {
            type: 'text/html',
            value: options.body.replace(/\n/g, '<br>'),
          },
        ],
        tracking_settings: {
          click_tracking: {
            enable: true,
          },
          open_tracking: {
            enable: true,
          },
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ errors: [{ message: response.statusText }] }));
      throw new Error(errorData.errors?.[0]?.message || 'SendGrid API error');
    }

    const messageId = response.headers.get('X-Message-Id') || `sg_${Date.now()}`;

    return {
      success: true,
      messageId,
    };
  } catch (error) {
    console.error('SendGrid email error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Send email using Nodemailer (SMTP)
 * Alternative to SendGrid for self-hosted SMTP
 */
export async function sendEmailWithNodemailer(_options: EmailOptions): Promise<EmailResult> {
  // Note: Nodemailer requires server-side Node.js environment
  // This is a placeholder for when we add Cloud Functions support
  console.warn('Nodemailer requires server-side implementation');
  return {
    success: false,
    error: 'Nodemailer not supported in browser - use Cloud Functions',
  };
}

/**
 * Send test email to verify Firebase extension configuration
 */
export async function sendTestEmail(recipientEmail: string): Promise<EmailResult> {
  return sendEmailWithFirebase({
    to: recipientEmail,
    subject: 'Test Email from DossiLoc Admin',
    body: `This is a test email from the DossiLoc Admin Dashboard.

If you received this email, your Firebase Trigger Email extension is working correctly!

Sent at: ${new Date().toLocaleString('en-CA', { timeZone: 'America/Toronto' })}

Email Method: Firebase Firestore Trigger Extension
Source: Admin Dashboard

---
DossiLoc - Complete rent collection automation for Canadian property managers`,
    from: 'noreply@dossiloc.ca',
    fromName: 'DossiLoc Admin',
  });
}

/**
 * Monitor email delivery status
 * Listen for delivery updates on a specific email document
 */
export async function monitorEmailDelivery(
  messageId: string,
  onStatusUpdate: (status: 'PENDING' | 'SUCCESS' | 'ERROR', info?: any) => void
): Promise<() => void> {
  const emailDocRef = doc(db, 'mail', messageId);
  
  const unsubscribe = onSnapshot(emailDocRef, (docSnapshot) => {
    const data = docSnapshot.data();
    
    if (data?.delivery) {
      const status = data.delivery.state;
      const info = data.delivery.info || data.delivery.error;
      
      console.log(`Email ${messageId} status:`, status, info);
      onStatusUpdate(status, info);
    }
  });
  
  return unsubscribe;
}

/**
 * Validate email configuration
 */
export function validateEmailConfig(): { configured: boolean; provider: string; message: string } {
  // Firebase extension is always available if Firebase is configured
  // No client-side API keys needed
  return {
    configured: true,
    provider: 'Firebase Trigger Email Extension',
    message: 'Using Firestore-based email triggers (server-side)',
  };
}

/**
 * Get email sending statistics from Firestore
 * Queries the 'mail' collection for delivery status
 */
export async function getEmailStats(days: number = 7): Promise<{
  sent: number;
  delivered: number;
  pending: number;
  failed: number;
} | null> {
  try {
    const { collection, query, where, getDocs, Timestamp } = await import('firebase/firestore');
    
    const startDate = Timestamp.fromDate(
      new Date(Date.now() - days * 24 * 60 * 60 * 1000)
    );
    
    // Query emails from last N days
    const q = query(
      collection(db, 'mail'),
      where('metadata.timestamp', '>=', startDate)
    );
    
    const snapshot = await getDocs(q);
    
    let sent = 0;
    let delivered = 0;
    let pending = 0;
    let failed = 0;
    
    snapshot.forEach(doc => {
      const data = doc.data();
      sent++;
      
      if (data.delivery) {
        switch (data.delivery.state) {
          case 'SUCCESS':
            delivered++;
            break;
          case 'ERROR':
            failed++;
            break;
          case 'PENDING':
            pending++;
            break;
        }
      } else {
        pending++; // No delivery status yet
      }
    });
    
    return {
      sent,
      delivered,
      pending,
      failed,
    };
  } catch (error) {
    console.error('Error fetching email stats:', error);
    return null;
  }
}
